import { apiFetch, v1Path } from './api';
import type { MemberDto, ProjectDto, StateDto } from './work-types';

/** A stream as seen from an initiative: everyone sees every stream, with its states and members. */
export type InitiativeStream = ProjectDto & { states: StateDto[]; members: MemberDto[] };

export function fetchInitiativeStreams(): Promise<InitiativeStream[]> {
  return apiFetch<{ items: InitiativeStream[] }>(v1Path('/initiatives/streams')).then((res) => res.items);
}

/**
 * States for the given projects, keyed by state id. Streams come from the initiative stream list,
 * so they resolve even for streams the viewer is not in; the hidden workspaces are read directly.
 */
export async function loadStatesById(projectIds: string[]): Promise<Map<string, StateDto>> {
  const states = new Map<string, StateDto>();
  if (projectIds.length === 0) return states;
  const streams = await fetchInitiativeStreams().catch(() => [] as InitiativeStream[]);
  const known = new Set(streams.map((stream) => stream.id));
  for (const stream of streams) {
    if (projectIds.includes(stream.id)) for (const state of stream.states) states.set(state.id, state);
  }
  const rest = projectIds.filter((id) => !known.has(id));
  const lists = await Promise.all(
    rest.map((projectId) =>
      apiFetch<{ items: StateDto[] }>(v1Path(`/projects/${projectId}/states`)).catch(() => ({ items: [] as StateDto[] })),
    ),
  );
  for (const list of lists) for (const state of list.items) states.set(state.id, state);
  return states;
}

export function createLaunchStep(initiativeId: string, body: Record<string, unknown>) {
  return apiFetch<unknown>(v1Path(`/initiatives/${initiativeId}/steps`), {
    method: 'POST',
    body: JSON.stringify(body),
  });
}
